import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  Modal,
  Skeleton,
  Typography,
  UploadCard,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { SplatCheckoutDocument } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  ErrorType,
  FileUploadError,
  handleFileSelected,
} from "components/molecules/UploadAttachments";
import { UploadErrorModal } from "components/molecules/UploadErrorModal";
import { Attachment, defaultAllowedFileTypes } from "utils/usageUtils";

const UTILITY_BILL_DOCUMENT_TYPE = "Utility Bill";
const UTILITY_BILL_FILE_INPUT_TEST_ID =
  "UploadSection-upload-button-hidden-input";
const DELETE_BLOCKED_MESSAGE =
  "Utility bills cannot be deleted after any proposal has been submitted.";

type DisplayFile = Attachment & {
  displayId: string;
  isPending: boolean;
  pendingIndex?: number;
};

type UtilityBillUploadCardProps = {
  title?: string;
  uploadedFiles: Attachment[] | SplatCheckoutDocument[];
  uploadedNewFiles: Attachment[];
  setUploadedNewFiles: Dispatch<SetStateAction<Attachment[]>>;
  onDeleteUploadedFile?: (
    file: Attachment & { documentId: string }
  ) => Promise<void> | void;
  getBillOnline?: () => void;
  utilityConnectionStatus?: string;
  documentLoading?: boolean;
  disabled?: boolean;
  setIsDirty?: (dirty: boolean) => void;
  onError?: (errors: FileUploadError[]) => void;
  isDeleteBlocked?: boolean;
};

const getPersistedDisplayId = (
  file: Attachment | SplatCheckoutDocument,
  index: number
) => file.documentId ?? `persisted-${index}-${file.documentName}`;

const buildDisplayFiles = (
  uploadedFiles: Attachment[] | SplatCheckoutDocument[],
  uploadedNewFiles: Attachment[]
): DisplayFile[] => {
  const persisted: DisplayFile[] = uploadedFiles.map((file, index) => ({
    ...(file as Attachment),
    displayId: getPersistedDisplayId(file, index),
    isPending: false,
  }));

  const pending: DisplayFile[] = uploadedNewFiles.map((file, index) => ({
    ...file,
    displayId: `pending-${index}-${file.documentName}`,
    isPending: true,
    pendingIndex: index,
  }));

  return [...persisted, ...pending];
};

const UtilityBillUploadCard = ({
  title = "Utility Bills",
  uploadedFiles,
  uploadedNewFiles,
  setUploadedNewFiles,
  onDeleteUploadedFile,
  getBillOnline,
  utilityConnectionStatus = "",
  documentLoading,
  disabled = false,
  setIsDirty = () => {},
  onError,
  isDeleteBlocked = false,
}: UtilityBillUploadCardProps) => {
  const [errors, setErrors] = useState<FileUploadError[]>([]);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [showDeleteBlockedModal, setShowDeleteBlockedModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<DisplayFile | null>(null);
  const [deletingFileIds, setDeletingFileIds] = useState<string[]>([]);

  const displayFiles = useMemo(
    () => buildDisplayFiles(uploadedFiles, uploadedNewFiles),
    [uploadedFiles, uploadedNewFiles]
  );

  const processFileSelection = useCallback(
    async (files: FileList) => {
      await handleFileSelected(files, {
        allowedFileTypes: defaultAllowedFileTypes,
        documentType: UTILITY_BILL_DOCUMENT_TYPE,
        onError: (fileErrors) => {
          setErrors(fileErrors);
          onError?.(fileErrors);
        },
        onUploadedNewFiles: setUploadedNewFiles,
        setIsDirty,
      });
    },
    [onError, setIsDirty, setUploadedNewFiles]
  );

  const handleSelect = (files: FileList) => {
    void processFileSelection(files);
  };

  const handleUploadError = (error: Error) => {
    const payload = (error as { payload?: { files?: File[] } }).payload;
    if (payload?.files?.length) {
      const fileErrors: FileUploadError[] = payload.files.map((file) => ({
        fileName: file.name,
        errorType: ErrorType.UPLOAD_FAILED,
      }));
      setErrors(fileErrors);
      onError?.(fileErrors);
      return;
    }
    setErrors([
      { fileName: error.message, errorType: ErrorType.UPLOAD_FAILED },
    ]);
  };

  const handleDeleteRequest = (file: DisplayFile) => {
    if (isDeleteBlocked && !file.isPending) {
      setShowDeleteBlockedModal(true);
      return;
    }
    setFileToDelete(file);
    setShowDeleteConfirmationModal(true);
  };

  const removePendingFile = (index: number) => {
    const updated = uploadedNewFiles.filter((_, i) => i !== index);
    setUploadedNewFiles(updated);
    setIsDirty(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) {
      return;
    }

    const file = fileToDelete;
    setShowDeleteConfirmationModal(false);
    setFileToDelete(null);

    if (file.isPending && file.pendingIndex !== undefined) {
      removePendingFile(file.pendingIndex);
      return;
    }

    if (file.documentId && onDeleteUploadedFile) {
      const deletingId = file.displayId;
      setDeletingFileIds((ids) => [...ids, deletingId]);
      try {
        await onDeleteUploadedFile(file as Attachment & { documentId: string });
      } finally {
        setDeletingFileIds((ids) => ids.filter((id) => id !== deletingId));
      }
    }
  };

  const downloadFile = async (file: DisplayFile) => {
    const response = await fetch(file.url);
    const blobFile = await response.blob();
    const blobUrl = URL.createObjectURL(blobFile);
    const alink = document.createElement("a");
    alink.href = blobUrl;
    alink.download = file.documentName;
    alink.target = "_blank";
    alink.rel = "noreferrer";
    document.body.appendChild(alink);
    alink.click();
    document.body.removeChild(alink);
    URL.revokeObjectURL(blobUrl);
  };

  const previewFile = (file: DisplayFile) => {
    window.open(file.url, "_blank", "noopener,noreferrer");
  };

  const isUploadDisabled = disabled || documentLoading;

  return (
    <div data-testid="utility-bill-attachment-upload-card">
      {documentLoading ? (
        <CardLoadingSkeletonWrapper data-testid="utility-bill-upload-loading-skeleton">
          <CardLoadingSkeleton />
        </CardLoadingSkeletonWrapper>
      ) : (
        <UploadCard
          title={title}
          files={displayFiles}
          accept={defaultAllowedFileTypes.join(",")}
          multiple
          disabled={isUploadDisabled}
          viewOnly={disabled}
          defaultEditMode
          hideEditButton
          getFileNameField={(file) => file.documentName}
          getFileIdField={(file) => file.displayId}
          deletingFileIds={deletingFileIds}
          onError={handleUploadError}
          onSelect={handleSelect}
          onDelete={handleDeleteRequest}
          onDownload={(file) => {
            void downloadFile(file);
          }}
          onPreview={previewFile}
          getBillOnline={getBillOnline}
          utilityConnectionStatus={utilityConnectionStatus}
        />
      )}
      <UploadErrorModal errors={errors} onClose={() => setErrors([])} />
      {showDeleteConfirmationModal && (
        <Modal
          onClose={() => {
            setShowDeleteConfirmationModal(false);
            setFileToDelete(null);
          }}
          type="destructive"
          title="Are you sure you want to permanently delete this file?"
          testId="utility-bill-attachment-confirm-modal"
          secondaryButton={{
            text: "Cancel",
            onClick: () => {
              setShowDeleteConfirmationModal(false);
              setFileToDelete(null);
            },
            testId: "cancel-delete-utility-bill-attachment-confirm-modal",
          }}
          primaryButton={{
            text: "Delete",
            onClick: () => {
              void confirmDelete().catch(() => {
                // Parent onDeleteUploadedFile surfaces errors (e.g. ErrorModal)
              });
            },
            testId: "remove-utility-bill-attachment-button",
          }}
        />
      )}
      {showDeleteBlockedModal && (
        <Modal
          onClose={() => setShowDeleteBlockedModal(false)}
          type="warning"
          title="Utility bills cannot be deleted"
          testId="utility-bill-delete-blocked-modal"
          primaryButton={{
            text: "Confirm",
            onClick: () => setShowDeleteBlockedModal(false),
            testId: "utility-bill-delete-blocked-modal-confirm",
          }}
        >
          <Typography>{DELETE_BLOCKED_MESSAGE}</Typography>
        </Modal>
      )}
    </div>
  );
};

const CardLoadingSkeletonWrapper = styled.div`
  width: 100%;
`;

const CardLoadingSkeleton = styled(Skeleton)`
  width: 100%;
  min-height: 160px;
  border-radius: 8px;
`;

export type { UtilityBillUploadCardProps, DisplayFile };
export {
  UtilityBillUploadCard,
  buildDisplayFiles,
  UTILITY_BILL_FILE_INPUT_TEST_ID,
};
